import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Monitor } from "lucide-react";

import { signIn } from "../auth/cognito";
import signInBackground from "../assets/signin-iot-bg.png";

export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // MfaChallengePage navigates back here with a friendly error when a
  // session-expired or otherwise terminal verify failure happens —
  // surface it on first paint so the user understands why they're back.
  const signInError = (location.state as { signInError?: string } | null)?.signInError ?? null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(signInError);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const resp = await signIn(email, password);
      if (resp.state === "authenticated") {
        navigate("/");
        return;
      }
      // Forced admin MFA enrolment — Cognito accepted the password
      // but the user holds an admin role with no TOTP factor yet.
      // Route to the dedicated enrolment page; access token rides in
      // location.state (memory-only, never URL or persisted).
      if (resp.state === "mfa_enrollment_required") {
        navigate("/mfa-enrollment", {
          replace: true,
          state: { accessToken: resp.accessToken, email },
        });
        return;
      }
      // Invited user's first sign-in — they signed in with the temporary
      // password and must choose a permanent one before proceeding.
      if (resp.state === "new_password_required") {
        navigate("/set-password", {
          replace: true,
          state: { session: resp.session, email },
        });
        return;
      }
      // Existing TOTP / passkey challenge OR new mfa_select picker.
      // Forward the session (and any factor-specific extras) to the
      // challenge page via location.state. MfaChallengePage branches
      // on `state` to pick the right verify endpoint.
      navigate("/mfa-challenge", {
        replace: true,
        state: {
          state: resp.state,
          session: resp.session,
          email,
          secretCode: resp.state === "mfa_setup" ? resp.secretCode : undefined,
          credentialRequestOptions:
            resp.state === "webauthn_required" ? resp.credentialRequestOptions : undefined,
          availableFactors:
            resp.state === "mfa_select" ? resp.availableFactors : undefined,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-riot-navy-950">
      <div className="absolute inset-0">
        <img
          src={signInBackground}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-riot-navy-950/58" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_22%,rgba(6,10,22,0.55)_52%,rgba(0,0,0,0.94)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.78)_0%,rgba(0,0,0,0.18)_24%,rgba(0,0,0,0.18)_76%,rgba(0,0,0,0.82)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.82)_0%,rgba(0,0,0,0.08)_22%,rgba(0,0,0,0.08)_74%,rgba(0,0,0,0.88)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.18),transparent_24%),radial-gradient(circle_at_bottom_left,rgba(66,153,225,0.18),transparent_30%)]" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-10 sm:px-8 lg:justify-end lg:pr-16 xl:pr-24">
        <div className="w-full max-w-sm">
          <div className="rounded-[28px] border border-white/12 bg-riot-navy-950/68 px-8 py-10 shadow-[0_28px_90px_rgba(2,8,23,0.55)] backdrop-blur-md">
            <div className="mb-8 flex flex-col items-center text-center">
              <div className="mb-4 inline-flex items-center rounded-full border border-white/10 bg-white/6 px-3 py-1 text-[11px] font-medium tracking-[0.18em] text-riot-gold-300 uppercase">
                Secure IoT fleet access
              </div>
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-riot-gold-400/12 ring-1 ring-riot-gold-400/25">
                <Monitor size={24} className="text-riot-gold-300" />
              </div>
              <h1 className="text-2xl font-bold text-white">RIoT-FOTA</h1>
              <p className="mt-2 max-w-xs text-sm text-riot-navy-200/80">
                Sign in to manage connected devices, staged rollouts, and secure over-the-air updates.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-xs font-medium text-riot-navy-100/90">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="w-full rounded-xl border border-white/10 bg-white/6 px-3 py-2.5 text-sm text-white shadow-sm placeholder:text-riot-navy-300/70 focus:border-riot-gold-400/70 focus:outline-none focus:ring-2 focus:ring-riot-gold-400/30"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-1 block text-xs font-medium text-riot-navy-100/90">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="w-full rounded-xl border border-white/10 bg-white/6 px-3 py-2.5 text-sm text-white shadow-sm placeholder:text-riot-navy-300/70 focus:border-riot-gold-400/70 focus:outline-none focus:ring-2 focus:ring-riot-gold-400/30"
                />
              </div>

              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-riot-gold-300 transition-colors hover:text-riot-gold-200">
                  Forgot password?
                </Link>
              </div>

              {error && (
                <div role="alert" className="rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950 shadow-[0_12px_30px_rgba(212,175,55,0.22)] transition hover:bg-riot-gold-300 active:bg-riot-gold-500 focus:outline-none focus:ring-2 focus:ring-riot-gold-300 focus:ring-offset-2 focus:ring-offset-riot-navy-950 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-riot-navy-200/75">
              Don&apos;t have an account?{" "}
              <Link to="/register" className="font-medium text-riot-gold-300 transition-colors hover:text-riot-gold-200">
                Register
              </Link>
            </p>
          </div>

          <p className="mt-4 text-center text-[11px] text-riot-navy-200/55">
            RIoT-FOTA Platform &middot; Riot IO (PTY) Ltd
          </p>
        </div>
      </div>
    </div>
  );
}
